import { db } from '../config/firebase';
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { writeAuditLog, writeDealAuditLog } from './auditService';

// ─── DEALS ────────────────────────────────────────────────────────────────────

export async function getDealById(dealId) {
  const snap = await getDoc(doc(db, 'deals', dealId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getDealsByFunds(fundIds, isAdmin = false) {
  // Scope to fundIds whenever any are provided — for EVERYONE, including admins
  // (so an admin who selects a specific fund sees only that fund).
  if (fundIds?.length) {
    const snap = await getDocs(
      query(collection(db, 'deals'), where('fund_id', 'in', fundIds))
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  // Empty fundIds: only admins may view across all funds. A non-admin with no
  // fund selected gets nothing (never all deals).
  if (!isAdmin) return [];
  const snap = await getDocs(collection(db, 'deals'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export function subscribeToDealsByFunds(fundIds, callback, isAdmin = false) {
  // Non-admin with no fund selected: return nothing (guard preserved).
  if (!isAdmin && !fundIds?.length) { callback([]); return () => {}; }
  // Scope to fundIds whenever any are provided — for EVERYONE, including admins.
  // Empty fundIds is only reachable here for an admin (guard above), → all deals.
  const q = fundIds?.length
    ? query(collection(db, 'deals'), where('fund_id', 'in', fundIds))
    : collection(db, 'deals');
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeToDeal(dealId, callback) {
  return onSnapshot(doc(db, 'deals', dealId), snap => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

export async function updateDeal({ dealId, updates, performedBy, action }) {
  await updateDoc(doc(db, 'deals', dealId), updates);
  if (action && performedBy) {
    await writeAuditLog({
      action,
      entityType: 'deal',
      entityId: dealId,
      performedBy,
      details: updates,
    });
  }
}

export async function assignAnalyst({ dealId, analystId, analystName, performedBy, performedByName }) {
  const updates = {
    assigned_analyst_id: analystId,
    assigned_analyst_name: analystName || null,
    current_stage: 'analyst_review',
    stage_entered_at: serverTimestamp(),
  };
  await updateDoc(doc(db, 'deals', dealId), updates);
  await writeAuditLog({
    action: 'analyst_assigned',
    entityType: 'deal',
    entityId: dealId,
    performedBy,
    performedByName,
    details: { analystId, analystName },
  });
  await writeDealAuditLog({
    dealId,
    action: 'analyst_assigned',
    performedBy,
    performedByName,
    details: { analystId, analystName },
    stageAtTime: 'analyst_review',
  });
}

export async function advanceDealStage({ dealId, newStage, extraUpdates = {}, performedBy, performedByName, action }) {
  const updates = {
    current_stage: newStage,
    stage_entered_at: serverTimestamp(),
    ...extraUpdates,
  };
  await updateDoc(doc(db, 'deals', dealId), updates);
  await writeAuditLog({
    action,
    entityType: 'deal',
    entityId: dealId,
    performedBy,
    performedByName,
    details: { newStage, ...extraUpdates },
  });
  await writeDealAuditLog({
    dealId,
    action,
    performedBy,
    performedByName,
    details: { newStage, ...extraUpdates },
    stageAtTime: newStage,
  });
}

export async function denyDeal({ dealId, reason, performedBy, performedByName, stageAtTime }) {
  await updateDoc(doc(db, 'deals', dealId), {
    status: 'denied',
    current_stage: 'denied',
    stage_entered_at: serverTimestamp(),
  });
  await writeAuditLog({
    action: 'deal_denied',
    entityType: 'deal',
    entityId: dealId,
    performedBy,
    performedByName,
    details: { reason },
  });
  await writeDealAuditLog({
    dealId,
    action: 'deal_denied',
    performedBy,
    performedByName,
    details: { reason },
    stageAtTime: stageAtTime || 'denied',
  });
}

export async function suspendDeal({ dealId, reason, reactivationDate, performedBy, performedByName, stageAtTime }) {
  const updates = {
    status: 'suspended',
    suspension_reason: reason,
    suspension_reactivation_date: reactivationDate || null,
  };
  await updateDoc(doc(db, 'deals', dealId), updates);
  await writeAuditLog({
    action: 'deal_suspended',
    entityType: 'deal',
    entityId: dealId,
    performedBy,
    performedByName,
    details: { reason, reactivationDate },
  });
  await writeDealAuditLog({
    dealId,
    action: 'deal_suspended',
    performedBy,
    performedByName,
    details: { reason, reactivationDate },
    stageAtTime,
  });
}

// ─── PIF SUBMISSIONS ──────────────────────────────────────────────────────────

export async function getPIFSubmissions(dealId) {
  const snap = await getDocs(
    query(
      collection(db, 'deals', dealId, 'pif_submissions'),
      orderBy('submitted_at', 'desc')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getCurrentPIFSubmission(dealId) {
  const snap = await getDocs(
    query(
      collection(db, 'deals', dealId, 'pif_submissions'),
      where('is_current', '==', true),
      limit(1)
    )
  );
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function confirmAISummary({
  dealId,
  pifSubmissionId,
  confirmedBy,
  confirmedByName,
  finalSummary,
  originalSummary,
}) {
  const updates = {
    ai_summary_confirmed: true,
    ai_summary_confirmed_by: confirmedBy,
    ai_summary_confirmed_by_name: confirmedByName || null,
    ai_summary_confirmed_at: serverTimestamp(),
  };
  // If analyst edited the summary, store both versions
  if (finalSummary) {
    updates.ai_summary_final = finalSummary;
  }
  if (originalSummary) {
    updates.ai_summary_original = originalSummary;
  }
  await updateDoc(
    doc(db, 'deals', dealId, 'pif_submissions', pifSubmissionId),
    updates
  );
  await writeDealAuditLog({
    dealId,
    action: 'ai_summary_confirmed',
    performedBy: confirmedBy,
    details: { pifSubmissionId },
    stageAtTime: null,
  });
}

// ─── ANALYST REVIEWS ──────────────────────────────────────────────────────────

export async function getAnalystReviews(dealId) {
  const snap = await getDocs(
    query(
      collection(db, 'deals', dealId, 'analyst_reviews'),
      orderBy('created_at', 'asc')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── IIC APPROVALS ────────────────────────────────────────────────────────────

export async function getIICApprovals(dealId) {
  const snap = await getDocs(
    query(
      collection(db, 'deals', dealId, 'iic_approvals'),
      orderBy('stage_order', 'asc')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── DEAL AUDIT LOG ───────────────────────────────────────────────────────────

export async function getDealAuditLog(dealId) {
  // Firestore orderBy on performed_at may not sort correctly when the field
  // is a mix of Timestamp (old serverTimestamp writes) and ISO string (new writes).
  // Client-side sort handles both types: Firestore Timestamps have a .toDate()
  // method; ISO strings are directly parseable by new Date().
  const snap = await getDocs(
    query(
      collection(db, 'deals', dealId, 'audit_log'),
      limit(50)
    )
  );
  const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  entries.sort((a, b) => {
    const toMs = v => (v?.toDate ? v.toDate() : new Date(v)).getTime();
    return toMs(b.performed_at) - toMs(a.performed_at);
  });
  return entries;
}

// ─── PIPELINE DASHBOARD DATA ──────────────────────────────────────────────────

export async function getRecentAuditLog(fundIds, count = 10) {
  if (!fundIds?.length) return [];
  const snap = await getDocs(
    query(
      collection(db, 'audit_log'),
      where('entity_type', '==', 'deal'),
      orderBy('performed_at', 'desc'),
      limit(count)
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// For non-admin/non-principal users: reads deal-level audit logs instead of
// the system-wide /audit_log collection (which requires admin/principal access).
export async function getRecentDealAuditLogs(deals, count = 10) {
  if (!deals?.length) return [];
  const recentActivity = [];
  // Sample the first 5 deals to keep get() calls bounded
  for (const deal of deals.slice(0, 5)) {
    const dealAuditSnap = await getDocs(
      query(
        collection(db, 'deals', deal.id, 'audit_log'),
        orderBy('performed_at', 'desc'),
        limit(3)
      )
    );
    dealAuditSnap.forEach(d =>
      recentActivity.push({ id: d.id, ...d.data(), dealId: deal.id })
    );
  }
  recentActivity.sort((a, b) =>
    new Date(b.performed_at) - new Date(a.performed_at)
  );
  return recentActivity.slice(0, count);
}

export async function getDealDocuments(dealId) {
  const snap = await getDocs(
    query(
      collection(db, 'deals', dealId, 'documents'),
      where('is_deleted', '==', false)
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
